import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ClientService } from './client.service';

export interface UserModel {
  id: number;
  email: string;
  password: string;
  name: string | null;
  username: string | null;
  description: string | null;
  avatarReadUrl?: string | null;
  avatarReadUrls?: UserAvatarReadUrls | null;
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
}

export interface UserAvatarReadUrls {
  size225: string | null;
  size160: string | null;
  size50: string | null;
}

export type UserUpsertPayload = Omit<
  UserModel,
  'createdAt' | 'lastLogin' | 'avatarReadUrl' | 'avatarReadUrls'
>;

export interface UserRegistrationPayload {
  name: string;
  email: string;
  password: string;
}

export interface PaymentActivationPayload {
  token: string;
  password: string;
  acceptTerms: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly client = inject(ClientService);
  private readonly currentUserState = signal<UserModel | null>(null);

  readonly currentUser = this.currentUserState.asReadonly();

  getById(id: number): Observable<UserModel> {
    return this.client
      .get<UserModel>(`user/${id}`)
      .pipe(tap((user) => this.currentUserState.set(user)));
  }

  create(payload: UserUpsertPayload): Observable<UserModel> {
    return this.client
      .post<UserModel, UserUpsertPayload>('user', payload)
      .pipe(tap((user) => this.currentUserState.set(user)));
  }

  register(payload: UserRegistrationPayload): Observable<UserModel> {
    const body = {
      name: payload.name.trim(),
      email: payload.email.trim(),
      password: payload.password,
    };

    return this.client.post<UserModel, typeof body>('user', body);
  }

  completePaymentActivation(
    payload: PaymentActivationPayload,
  ): Observable<{ success: boolean }> {
    return this.client.post<{ success: boolean }, PaymentActivationPayload>(
      'auth/activation/complete',
      payload,
    );
  }

  update(payload: UserUpsertPayload): Observable<UserModel> {
    return this.client
      .put<UserModel, UserUpsertPayload>('user', payload)
      .pipe(tap((user) => this.currentUserState.set(user)));
  }

  uploadAvatar(file: File): Observable<UserModel> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.client
      .postForm<UserModel>('user/avatar', formData)
      .pipe(tap((user) => this.currentUserState.set(user)));
  }
}
